/**
 * ContactCard — Deal Detail right-rail card showing who is selling.
 *
 * Phase 36. Renders four states:
 *   1. Empty state                → "No contact info yet · Add contact"
 *   2. Realtor listing            → "Realtor · {brokerage}" header strip
 *   3. FSBO listing               → green "FSBO" pill instead of brokerage row
 *   4. Wholesaler / Off-market    → small uppercase label
 *
 * All channel rows use semantic anchors:
 *   • email → <a href="mailto:..">
 *   • phone → <a href="tel:+..">
 *   • cell  → ditto; hidden when cell == phone OR contact_phone_same_as_cell
 *
 * "Edit contact" navigates to the wizard at Step 2 (Property), which is
 * where the Listing & contact section lives.
 */
import { Building, ExternalLink, Mail, Phone, Smartphone, StickyNote, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { detectListingPlatform } from "@/components/property/PropertyMeta";

const LISTING_LABEL = {
  realtor: "Realtor",
  for_sale_by_owner: "FSBO",
  wholesaler: "Wholesaler",
  off_market: "Off-market",
  unknown: "Listing",
};

// Mirrors the backend canonical form: digits-only or "+digits".
// Displays as "(305) 555-1234" for US 10-digit numbers and "+1 305-555-1234"
// for international. Anything else is returned verbatim.
function formatPhoneDisplay(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const digits = s.replace(/\D/g, "");
  if (s.startsWith("+1") && digits.length === 11) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return s.startsWith("+") ? `+${digits}` : digits || s;
}

function telHref(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  const digits = s.replace(/\D/g, "");
  if (!digits) return null;
  return s.startsWith("+") ? `tel:+${digits}` : `tel:${digits}`;
}

export default function ContactCard({ deal }) {
  const navigate = useNavigate();
  const lt = deal?.listing_type || "unknown";
  const name = (deal?.contact_name || "").trim();
  const brokerage = (deal?.contact_brokerage || "").trim();
  const email = (deal?.contact_email || "").trim();
  const phone = (deal?.contact_phone || "").trim();
  const cell = (deal?.contact_cell || "").trim();
  const sameAsPhone = !!deal?.contact_phone_same_as_cell || (cell && cell === phone);
  const notes = (deal?.contact_notes || "").trim();
  const listingUrl = (deal?.listing_url || "").trim();
  const platform = listingUrl ? detectListingPlatform(listingUrl) : null;
  const hasAny = !!(name || brokerage || email || phone || cell || notes || listingUrl) || (lt && lt !== "unknown");

  const goEdit = () => navigate(`/deals/${deal?.id}/edit?step=2`);

  return (
    <div
      data-testid="deal-contact-card"
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Contact
        </p>
        {hasAny ? (
          <button
            type="button"
            onClick={goEdit}
            data-testid="contact-edit-btn"
            className="text-xs font-medium text-indigo-600 hover:underline"
          >
            Edit contact
          </button>
        ) : null}
      </div>

      {!hasAny ? (
        <div className="mt-3 text-sm text-slate-500">
          <p>No contact info yet.</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={goEdit}
            data-testid="contact-add-btn"
          >
            Add contact
          </Button>
        </div>
      ) : (
        <div className="mt-3 space-y-2.5">
          {/* Listing type strip */}
          {lt === "realtor" ? (
            <p className="flex items-center gap-1.5 text-xs text-slate-500">
              <Building className="h-3.5 w-3.5" aria-hidden="true" />
              <span>
                Realtor{brokerage ? <> — <span className="text-slate-700">{brokerage}</span></> : null}
              </span>
            </p>
          ) : lt === "for_sale_by_owner" ? (
            <span
              data-testid="fsbo-badge"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200"
            >
              FSBO
            </span>
          ) : lt && lt !== "unknown" ? (
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {LISTING_LABEL[lt] || lt}
            </p>
          ) : null}

          {/* Name */}
          {name ? (
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <User className="h-4 w-4 text-slate-400" aria-hidden="true" />
              {name}
            </p>
          ) : null}

          {/* Listing URL — placed below the name so the link to "where this
              came from" is the first action after the name row. */}
          {listingUrl ? (
            <a
              href={listingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-listing-url-link"
              className="flex items-center gap-2 text-sm text-slate-700 hover:text-indigo-600"
            >
              <ExternalLink className="h-4 w-4 text-slate-400" aria-hidden="true" />
              {platform ? (
                <span
                  data-testid="contact-listing-platform-badge"
                  className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${platform.badgeClass}`}
                >
                  {platform.label}
                </span>
              ) : null}
              <span className="hover:underline">View listing</span>
              <span aria-hidden="true">→</span>
            </a>
          ) : null}

          {/* Email */}
          {email ? (
            <a
              href={`mailto:${email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-2 text-sm text-slate-700 hover:text-indigo-600 hover:underline"
            >
              <Mail className="h-4 w-4 text-slate-400" aria-hidden="true" />
              <span className="truncate">{email}</span>
            </a>
          ) : null}

          {/* Phone */}
          {phone ? (
            <a
              href={telHref(phone)}
              data-testid="contact-phone-link"
              className="flex items-center gap-2 text-sm text-slate-700 hover:text-indigo-600 hover:underline"
            >
              <Phone className="h-4 w-4 text-slate-400" aria-hidden="true" />
              <span>{formatPhoneDisplay(phone)}</span>
            </a>
          ) : null}

          {/* Cell (only when distinct from phone) */}
          {cell && !sameAsPhone ? (
            <a
              href={telHref(cell)}
              data-testid="contact-cell-link"
              className="flex items-center gap-2 text-sm text-slate-700 hover:text-indigo-600 hover:underline"
            >
              <Smartphone className="h-4 w-4 text-slate-400" aria-hidden="true" />
              <span>{formatPhoneDisplay(cell)}</span>
            </a>
          ) : null}

          {/* Notes */}
          {notes ? (
            <div className="mt-1 rounded-lg bg-slate-50 p-2.5">
              <p className="flex items-start gap-2 text-xs text-slate-600">
                <StickyNote className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" aria-hidden="true" />
                <span className="whitespace-pre-line">{notes}</span>
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
